import { createSelector } from "@reduxjs/toolkit"
import { ContractFunnelDto } from "api/pricing"
import { selectPricingState } from "."

// Types de filtres supportés
type FilterType = 'INSURER' | 'SUPPORT_TYPE' | string;

// Mapping des types de filtre vers les champs du contractFunnel
const FILTER_TYPE_TO_FUNNEL_FIELD: Record<FilterType, (funnel: ContractFunnelDto) => string | undefined> = {
  'INSURER': (funnel) => funnel.product?.owner?.id,
  'SUPPORT_TYPE': (funnel) => funnel.supportId,
};

// Sélecteur pour les filtres actifs (par type au lieu du titre)
export const selectActiveFilters = createSelector(
  [selectPricingState],
  (pricingState) => {
    const activeFilters: { [key: string]: string[] } = {}

    pricingState.filters.forEach(filter => {
      const activeItems = filter.items
        .filter(item => item.value)
        .map(item => item.id)

      if (activeItems.length > 0) {
        // Utiliser le type du filtre au lieu du titre
        activeFilters[filter.type] = activeItems
      }
    })

    return activeFilters
  }
)

// Sélecteur pour les tunnels filtrés
export const selectFilteredContractTunnels = createSelector(
  [selectPricingState, selectActiveFilters],
  (pricingState, activeFilters) => {
    const { contractFunnels } = pricingState
    if (!contractFunnels) {
      return []
    }

    // Si aucun filtre n'est actif, retourner tous les tunnels
    if (Object.keys(activeFilters).length === 0) {
      return contractFunnels
    }

    return contractFunnels.filter(contractFunnel => {
      // Cartouche inéligible (isSuccess=false) : toujours affichée (grisée + message),
      // on ne la filtre pas par attribut produit — son supportId/assureur peut être vide.
      if (contractFunnel.isSuccess === false) {
        return true
      }

      // Parcourir tous les types de filtres actifs
      for (const [filterType, activeIds] of Object.entries(activeFilters)) {
        // Récupérer la fonction de mapping pour ce type de filtre
        const getFunnelField = FILTER_TYPE_TO_FUNNEL_FIELD[filterType as FilterType]

        if (getFunnelField) {
          const funnelFieldValue = getFunnelField(contractFunnel)
          // Si le funnel n'a pas de valeur pour ce champ ou si elle n'est pas dans les IDs actifs
          if (!funnelFieldValue || !activeIds.includes(funnelFieldValue)) {
            return false
          }
        }
      }

      return true
    })
  }
)


export const selectFilters = createSelector(
  [selectPricingState],
  (pricingState) => pricingState.filters
)

